/**
 * AW-2B3.2 -- the rules that make ScriptPlay's embedded (Production
 * Agent Workspace) mode safe.
 *
 * Kept here, beside the runner's other utilities, rather than inside
 * the 3,300-line page: these three functions ARE the authority
 * contract, and they must be testable as values. ScriptPlay itself
 * cannot be imported under Jest in this repository (react-router-dom 7
 * declares a `main` it does not ship, and the page pulls 38 further
 * imports behind path aliases), so a rule that lived only inside it
 * could not be proven at all.
 */

/**
 * Normalizes the `embedded` prop, all-or-nothing.
 *
 * `null`/`undefined` is the legacy `/scripts/:id/play` route. An object
 * must carry BOTH identities: a half-supplied prop is never allowed to
 * fall back to the URL, because "Script from the workspace, run from
 * the address bar" is exactly the browser-chosen Script authority
 * AW-2B2 exists to prevent. An incomplete object therefore resolves to
 * `invalid`, which the page renders as an error rather than running
 * anything.
 */
export function normalizeEmbeddedRun(embedded) {
  if (embedded === null || embedded === undefined) return null;
  const scriptId = String(embedded.scriptId || "").trim();
  const runId = String(embedded.runId || "").trim();
  if (!scriptId || !runId) return { invalid: true, scriptId: null, runId: null };
  return { invalid: false, scriptId, runId };
}

/**
 * The embedded runner-payload URL.
 *
 * Every Script and node payload request in embedded mode carries the
 * run id, because without it the backend serves live Script content --
 * the version-lock bypass this slice exists to close. A missing run id
 * throws rather than producing an unlocked path: there is deliberately
 * no request that could be issued here without one.
 */
export function buildLockedRunnerPath(path, runId) {
  if (!runId) throw new Error("aw2b3_embedded_run_required");
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}run_id=${encodeURIComponent(runId)}`;
}

/**
 * Embedded mode requires a provably version-locked payload.
 *
 * The backend sets `version_locked: true` only when it served from the
 * run's snapshot. Anything else -- absent, false, a shape change --
 * means the response may be live content, which an agent must never be
 * shown for a run locked to a publication. Failing closed is the only
 * safe answer: the alternative renders a Script the call is not running.
 */
export function assertLockedPayload(data) {
  if (data?.version_locked !== true) {
    throw new Error("aw2b3_unlocked_runner_payload");
  }
  return data;
}
